import React, { useCallback, useImperativeHandle, useState } from "react";
import type { Ref } from "react";
import { isFunction } from "@common/helpers/typeGuards";
import { OAuthServiceType } from "@common/constants";
import { Entity } from "@common/types/entity";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { useInteraction } from "@hooks/useInteraction";
import { useEntities } from "@hooks/useEntities";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";
import { useGustoConnectionCreationMutation } from "@mutations/gustoConnection";
import {
    ConnectionActionButton,
    ConnectionButtonSize,
    ConnectionButtonVariant,
} from "@components/financialConnection/ConnectionActionButton";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    AddAccountProps,
    CreateAccountHandle,
} from "../accounts/AddAccounts/lib";

interface Props extends AddAccountProps {
    children?:
        | React.ReactNode
        | ((props: {
              connecting: boolean;
              initializeConnection: () => void;
          }) => React.ReactNode);
    defaultEntity?: Entity;
    btnVariant?: ConnectionButtonVariant;
    btnSize?: ConnectionButtonSize;
}

// NOTE: CreateGustoConnection component seems to be mounted multiple times across different components, and I wasn't able
// to find a quick way to deduplicate same requests via React State. Feel free to refactor
let lastCodeRef: string | undefined;

export const CreateGustoConnection = ({
    children = <>Connect</>,
    onCreated,
    defaultEntity: defaultEntityProp,
    btnVariant = "secondary",
    btnSize = "md",
    ref,
}: Props & { ref?: Ref<CreateAccountHandle> }) => {
    const mutation = useGustoConnectionCreationMutation(onCreated);
    const [creating, setCreating] = useState(false);

    const businessEntities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const defaultEntity =
        defaultEntityProp ??
        (businessEntities.length === 1 ? businessEntities[0] : undefined);

    const handler = useCallback(
        async ({ code }: OAuthAuthorizationResponse) => {
            if (!creating || lastCodeRef === code) {
                return;
            }
            setCreating(false);
            lastCodeRef = code;
            await mutation.mutateAsync({
                code,
                defaultEntityId: defaultEntity?.id,
            });
            lastCodeRef = undefined;
        },
        [creating, mutation, defaultEntity?.id],
    );

    useWindowMessage<OAuthAuthorizationResponse>({
        scope: OAuthServiceType.GUSTO,
        handler,
    });

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const initializeConnection = useCallback(() => {
        if (creating) {
            return;
        }

        // The next step leaves the app, so the server never gets to refuse
        // it. Refuse here instead of after the provider's consent screen.
        if (refuseIfViewOnly()) {
            return;
        }

        triggerAuthorization(OAuthServiceType.GUSTO);
        setCreating(true);
    }, [creating, refuseIfViewOnly]);

    const authorize = useInteraction(initializeConnection);

    useImperativeHandle(
        ref,
        () => ({
            initializeConnection,
        }),
        [initializeConnection],
    );

    if (isFunction(children)) {
        return children({ connecting: creating, initializeConnection });
    }

    return (
        <span {...authorize}>
            <ConnectionActionButton
                loading={mutation.isPending}
                variant={btnVariant}
                size={btnSize}
            >
                {children}
            </ConnectionActionButton>
        </span>
    );
};
